import {Text} from '@/components-v2/atoms'
import {LabelValue} from '@/components-v2/molecules'
import {Modal} from '@/components-v2/organisms/Modal/Modal'
import {usePromotionConfig} from '@/hooks/usePromotionConfig'
import {PromotionStep} from '@/types/promotion'
import {displayOnMapping} from '@/utils/constants/promotionConstants'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import {useState} from 'react'
import {PromotionReviewSection} from './PromotionReviewSection'

/**
 * @param {{
 *  promotionReviewData: import('@/types/promotion').Promotion,
 *  showEdit: boolean,
 *  setCurrentStep: (step: import('@/types/promotion').PromotionStep) => void,
 *  languages: Array<{code: string, language: string}>,
 * }} props
 */
export const PromotionReviewDisplay = ({promotionReviewData, showEdit, setCurrentStep, languages}) => {
  const {promotionConfig} = usePromotionConfig()
  const [showImagePreview, setShowImagePreview] = useState(false)

  const getDisplayOnText = () => {
    return promotionReviewData?.displayOn?.map(item => displayOnMapping[item] || item).join(', ') || '-'
  }

  return (
    <>
      <PromotionReviewSection
        label="Display"
        showEdit={showEdit}
        onEdit={() => setCurrentStep(PromotionStep.DISPLAY)}
      >
        <div className="flex w-full flex-col gap-6 overflow-hidden">
          {/* Banner & display settings */}
          <div>
            <div className="grid grid-cols-3 items-center gap-x-4 py-4">
              <div
                className="relative w-full max-w-[156px] cursor-pointer overflow-hidden rounded-lg border border-dotted border-[var(--color-border-default)]"
                onClick={() => setShowImagePreview(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setShowImagePreview(true)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {promotionReviewData?.bannerUrl ? (
                  <>
                    <img
                      src={promotionReviewData.bannerUrl}
                      alt="Promotion Banner"
                      className="block h-auto w-full object-contain"
                    />
                    <div className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded bg-black/80 p-1">
                      <OpenInFullIcon style={{fontSize: '16px', color: 'white'}} />
                    </div>
                  </>
                ) : (
                  <div className="aspect-[156/100] w-full bg-[var(--color-background-layer-02)]" />
                )}
              </div>
              <LabelValue keyName="Promotion Shows On" val={getDisplayOnText()} />
              <LabelValue keyName="Display Order" val={promotionReviewData?.displayOrder?.toString() || '-'} />
            </div>
          </div>

          {/* Banner type / landing page row */}
          <div>
            <div className="h-px bg-[var(--secondary-800)]" />
            <div className="grid grid-cols-3 gap-x-4">
              <LabelValue
                keyName="Banner Type"
                val={
                  promotionReviewData?.bannerType
                    ? promotionReviewData.bannerType.charAt(0).toUpperCase() + promotionReviewData.bannerType.slice(1)
                    : '-'
                }
              />
              <LabelValue
                keyName="Landing Page"
                val={
                  promotionReviewData?.landingPage
                    ? promotionConfig?.landingPage?.find(p => p.value === promotionReviewData.landingPage)?.label ||
                      promotionReviewData.landingPage
                    : '-'
                }
              />
            </div>
          </div>

          {/* Per-language text */}
          <div className="grid grid-cols-1 divide-y divide-[var(--secondary-800)]">
            {languages.map(({code, language}) => (
              <div key={code} className="py-6">
                <Text size="small" variant="bold">
                  <span className="font-normal">Language: </span>
                  {language}
                </Text>
                <div className="grid grid-cols-3 gap-x-4">
                  <LabelValue keyName="Product Tag" val={promotionReviewData?.text?.[code]?.productTag || '-'} />
                  <LabelValue keyName="Banner Tag" val={promotionReviewData?.text?.[code]?.bannerTag || '-'} />
                  <LabelValue keyName="Excerpt" val={promotionReviewData?.text?.[code]?.excerpt || '-'} />
                  <LabelValue keyName="Title" val={promotionReviewData?.text?.[code]?.title || '-'} />
                  <LabelValue keyName="Description" val={promotionReviewData?.text?.[code]?.description || '-'} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </PromotionReviewSection>

      {showImagePreview && (
        <Modal
          showModal={showImagePreview}
          onCloseModal={() => setShowImagePreview(false)}
          title="Preview of Promo"
          showSlot={true}
          showPrimaryButton={false}
          showTertiaryButton={false}
        >
          <div className="relative mx-auto w-full max-w-2xl pt-4">
            <div className="relative overflow-hidden rounded-lg border border-dotted border-[var(--color-border-default)]">
              <img
                src={promotionReviewData?.bannerUrl || ''}
                alt="Promotion Banner Preview"
                className="block h-auto w-full object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h2 className="text-2xl font-bold text-white">
                  {promotionReviewData?.promotionName ||
                    promotionReviewData?.text?.en?.title ||
                    'Promotion Title'}
                </h2>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
